/**
 * ClientCatalogBanner — 3PL client-catalog routing (/products).
 *
 * Cross-org context cue for the client-catalog view. Same material and voice
 * as `SharedLocationContextBanner` (DESIGN.md §13.8): brand-purple bar,
 * `Link2` provenance mark, polite live region, 220ms entrance.
 *
 * Two deliberate differences from §13.8:
 *  1. It sits BELOW the sticky `PageActionBar`, in the page body, so it does
 *     not fight the bar for the sticky slot (that stacking dance is what
 *     `--shared-location-banner-height` exists to solve on /inventory, and the
 *     tabs + filters in the Products bar are already a tall surface). It is
 *     `rounded-md` rather than full-bleed for the same reason.
 *  2. It is not sticky. The persistent cue is the header switcher, which stays
 *     visible in the (sticky) TopHeader for the whole scroll.
 *
 * It also carries the authority boundary, not just the identity: the operator
 * needs to know what they CAN change here before they discover it by clicking
 * a dead row.
 */
import { motion } from 'framer-motion';
import { Link2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';

interface ClientCatalogBannerProps {
  /** Client org display name (alias-preferred). */
  clientName: string;
  /** Returns the page to the caller's own catalog. */
  onReset: () => void;
}

export function ClientCatalogBanner({ clientName, onReset }: ClientCatalogBannerProps) {
  const { t } = useTranslation('inventory');
  return (
    <motion.div
      role="status"
      aria-live="polite"
      initial={{ y: -8, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: -8, opacity: 0 }}
      transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
      className="mt-4 rounded-md bg-threadheads-purple text-white shadow-sm"
    >
      <div className="flex items-center gap-3 px-4 py-2.5 text-sm">
        <Link2 className="h-4 w-4 shrink-0" aria-hidden="true" />
        <p className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="font-semibold">
            {t('clientCatalog.banner.viewing', { client: clientName })}
          </span>
          <span className="text-xs text-white/75">
            {t('clientCatalog.banner.detail', { client: clientName })}
          </span>
        </p>
        <Button
          variant="ghost"
          size="sm"
          onClick={onReset}
          className="h-7 shrink-0 px-2 text-xs text-white hover:bg-white/15 hover:text-white"
        >
          {t('clientCatalog.banner.reset')}
        </Button>
      </div>
    </motion.div>
  );
}
